import {AssetColourComponent, AssetPart, AssetStatus, AssetUtils} from '@taopix/taopix-core';
import {AssetFPOInterface, AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';
import {CanvasImage, dataURItoBlob, drawImageToCanvas} from '../../Utils/Asset';

type AssetFunctionType = (asset: AssetMetaInterface, fpo: AssetFPOInterface | null) => void;

type LoaderProps = {
  onAssetError?: AssetFunctionType;
  onAssetLoaded?: AssetFunctionType;
};

export default class ImageLoader {
  private onAssetError: AssetFunctionType | undefined;
  private onAssetLoaded: AssetFunctionType | undefined;
  private cache: Map<string, string> = new Map();

  constructor(payload: LoaderProps) {
    this.onAssetError = payload?.onAssetError;
    this.onAssetLoaded = payload?.onAssetLoaded;
  }

  public load = (
    asset: AssetMetaInterface,
    isSmallScreen = false,
    height = 0,
    width = 0,
    part: AssetPart = AssetPart.Intermediate
  ): Promise<string> => {
    // if the asset is not processed, use the thumbnailsrc
    if (AssetStatus.Processed !== asset.status) {
      return Promise.resolve(asset.thumbnailsrc);
    }

    // if the asset has been cached, return it /* TODO - monitor this */
    if (this.cache.has(asset.globalAssetId)) {
      return Promise.resolve(this.cache.get(asset.globalAssetId) as string);
    }

    // if the asset has an alpha channel, blend it
    if (asset.hasAlpha) {
      return this.loadFlattenAlphaImage(asset, height, width, part, isSmallScreen)
        .then(payload => {
          this.cache.set(asset.globalAssetId, payload);
          return payload;
        })
        .catch(error => {
          return Promise.reject(error);
        });
    }

    // return a normal FPO
    return this._load(asset, height, width, part, AssetColourComponent.Image)
      .then(payload => {
        this.cache.set(asset.globalAssetId, payload.src);
        return payload.src;
      })
      .catch(error => {
        return Promise.reject(error);
      });
  };

  private loadFlattenAlphaImage = (
    asset: AssetMetaInterface,
    height: number,
    width: number,
    part: AssetPart,
    isSmallScreen: boolean
  ): Promise<string> => {
    if (isSmallScreen) {
      return Promise.resolve(asset.thumbnailsrc);
    }

    return new Promise<string>((resolve, reject) => {
      this._load(asset, height, width, part, AssetColourComponent.Image)
        .then((rgbCanvasData: CanvasImage) => {
          this._load(asset, height, width, part, AssetColourComponent.Alpha)
            .then((alphaCanvasData: CanvasImage) => {
              const rgbCanvas: HTMLCanvasElement = drawImageToCanvas(rgbCanvasData);
              let mergedCanvas: HTMLCanvasElement = drawImageToCanvas(
                alphaCanvasData,
                rgbCanvas,
                true
              );

              // eslint-disable-next-line node/no-unsupported-features/node-builtins
              const imageSrc = window.URL.createObjectURL(
                dataURItoBlob(mergedCanvas.toDataURL('image/png', 0.9))
              );

              // destroy the canvas (/* TODO - monitor this */)
              mergedCanvas.height = 0;
              mergedCanvas.width = 0;
              mergedCanvas.remove();
              (mergedCanvas as any) = null;

              resolve(imageSrc);
            })
            .catch(error => reject(error));
        })
        .catch(error => reject(error));
    });
  };

  /**
   * Load an asset from the server.
   *
   * @param   {AssetMetaInterface}         asset       the asset
   * @param   {number}                      height      the required height
   * @param   {number}                      width       the required width
   * @param   {AssetColourComponent}        colourComp  the colour composition
   * @param   {AssetPart}                   part        the generated part
   *
   * @return  {AssetFPOInterface}
   */
  private _load = (
    asset: AssetMetaInterface,
    height = 0,
    width = 0,
    part: AssetPart,
    colourComp: AssetColourComponent
  ): Promise<CanvasImage> => {
    const {rotation} = asset;

    return new Promise((resolve, reject) => {
      this.getBestIntermediate(asset, part, colourComp, height, width)
        .then(fpo => {
          const img = new Image();
          img.crossOrigin = 'Anonymous';

          img.id = 'image_' + asset.id;

          img.onload = () => {
            if (this.onAssetLoaded) {
              this.onAssetLoaded(asset, fpo);
            }

            resolve({
              height: fpo.height,
              src: fpo.src,
              width: fpo.width,
              rotation: rotation,
              processed: true,
              img: img,
            });
          };

          img.src = fpo.src;
        })
        .catch(error => {
          if (this.onAssetError) {
            this.onAssetError(asset, null);
          }
          reject(error);
        });
    });
  };

  /**
   * get the best sized FPO for a specific height & width.
   *
   * @param   {AssetMetaInterface}         asset       the asset
   * @param   {AssetPart}                   part        the asset part
   * @param   {AssetColourComponent}        colourComp  the colour composition
   * @param   {number}                      height      the required height
   * @param   {number}                      width       the required width
   *
   * @return  {AssetFPOInterface}
   */
  public findIntermediate = (
    asset: AssetMetaInterface,
    part: AssetPart,
    colourComp: AssetColourComponent,
    height: number,
    width: number
  ): AssetFPOInterface => {
    const children = asset.children.filter(
      // eslint-disable-next-line eqeqeq
      (fpo: AssetFPOInterface) => fpo.part == part && fpo.colourComponent === colourComp
    );

    const candidates: AssetFPOInterface[] | null = children.filter((child: AssetFPOInterface) => {
      return Math.ceil(child.width) >= width && Math.ceil(child.height) >= height;
    });

    let fpo: AssetFPOInterface | null = candidates.length
      ? (candidates.pop() as AssetFPOInterface)
      : null;

    if (null === fpo) {
      // default the result to the last FPO
      fpo = [...children].pop() as AssetFPOInterface;
    }

    return fpo;
  };

  /**
   * get the best sized FPO for a specific height & width. If the asset has expired resign it.
   *
   * @param   {AssetMetaInterface}         asset       the asset
   * @param   {AssetPart}                   part        the asset part
   * @param   {AssetColourComponent}        colourComp  the colour composition
   * @param   {number}                      height      the required height
   * @param   {number}                      width       the required width
   *
   * @return  {AssetFPOInterface}
   */
  private getBestIntermediate = (
    asset: AssetMetaInterface,
    part: AssetPart,
    colourComp: AssetColourComponent,
    height: number,
    width: number
  ): Promise<AssetFPOInterface> => {
    // filter the children by part & colour composition
    const fpo = this.findIntermediate(asset, part, colourComp, height, width);

    return new Promise((resolve, reject) => {
      if (!fpo) {
        reject(
          'No intermediate found from: ' +
            height +
            ' X ' +
            width +
            ', part: ' +
            part +
            ', comp: ' +
            colourComp
        );
      }

      if (AssetUtils.isFPOExpired(fpo)) {
        AssetUtils.resignFPO(asset, fpo, true).then(res => resolve(res));
      } else {
        resolve(fpo);
      }
    });
  };
}
